import { useCallback, useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button, Field, Select, Text } from "@/components/ui";
import { apiErrorText } from "@/lib/api-error";
import {
  decideJoinRequest,
  fetchJoinRequests,
  type JoinRequest,
  type JoinRequestStatus,
  type PageMeta,
} from "@/services/api/allowlist";

const PAGE_SIZE = 50;

/**
 * §31 Admin → Join requests: people who tried to register with an email that is not on the list.
 * Approve puts the email on the list and emails "you can now register"; decline keeps them out.
 */
export function JoinRequestsAdmin() {
  const t = useTranslations("allowlist");
  const tRoot = useTranslations();
  const locale = useLocale();
  const [status, setStatus] = useState<JoinRequestStatus>("pending");
  const [page, setPage] = useState(1);
  const [requests, setRequests] = useState<JoinRequest[] | null>(null);
  const [meta, setMeta] = useState<PageMeta>({ total: 0, page: 1, limit: PAGE_SIZE });
  const [loadFailed, setLoadFailed] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (s: JoinRequestStatus, p: number) => {
    try {
      const result = await fetchJoinRequests({ status: s, page: p, limit: PAGE_SIZE });
      setRequests(result.requests);
      setMeta(result.meta);
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void load(status, page);
  }, [load, status, page]);

  async function decide(request: JoinRequest, decision: "approve" | "decline") {
    setBusyId(request.id);
    setError(null);
    setNotice(null);
    try {
      const result = await decideJoinRequest(request.id, decision);
      setNotice(
        decision === "decline"
          ? t("declinedNotice", { email: request.email })
          : result.emailQueued === false
            ? t("approvedNoEmail", { email: request.email })
            : result.emailWaiting
              ? t("approvedEmailWaiting", { email: request.email })
              : t("approvedNotice", { email: request.email }),
      );
    } catch (err) {
      setError(
        apiErrorText(err, tRoot, "allowlist.decideFailed", { namespace: "allowlist.errors" }),
      );
    } finally {
      setBusyId(null);
    }
    await load(status, page);
  }

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(locale === "hi" ? "hi-IN" : "en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  const pages = Math.max(1, Math.ceil(meta.total / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="join-requests-admin">
      <Field label={t("statusLabel")} className="max-w-xs">
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as JoinRequestStatus);
            setPage(1);
          }}
          data-testid="join-requests-status"
        >
          <option value="pending">{t("statusPending")}</option>
          <option value="approved">{t("statusApproved")}</option>
          <option value="declined">{t("statusDeclined")}</option>
        </Select>
      </Field>
      {notice ? (
        <p role="status" className="text-sm" data-testid="join-requests-notice">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-[var(--danger)]" data-testid="join-requests-error">
          {error}
        </p>
      ) : null}
      {loadFailed ? (
        <p
          role="alert"
          className="text-sm text-[var(--danger)]"
          data-testid="join-requests-load-error"
        >
          {t("requestsLoadFailed")}
        </p>
      ) : requests === null ? (
        <Text tone="muted">{t("loading")}</Text>
      ) : requests.length === 0 ? (
        <Text tone="muted" data-testid="join-requests-empty">
          {t("requestsEmpty")}
        </Text>
      ) : (
        <ul className="divide-y divide-[var(--border)] rounded-lg border border-[var(--border)]">
          {requests.map((request) => (
            <li
              key={request.id}
              className="flex flex-wrap items-start justify-between gap-3 px-4 py-3"
              data-testid={`join-request-${request.email}`}
            >
              <div className="min-w-0 space-y-0.5">
                <p className="font-medium">{request.name ?? request.email}</p>
                {request.name ? <p className="text-sm">{request.email}</p> : null}
                <p className="text-xs text-[var(--text-muted)]">
                  {t("requestedAt", { date: formatDate(request.lastRequestedAt) })} ·{" "}
                  {t("requestCount", { count: request.requestCount })}
                  {request.ipAddress ? ` · ${t("requestFrom", { ip: request.ipAddress })}` : ""}
                </p>
                {request.message ? (
                  <p className="whitespace-pre-line break-words text-sm text-[var(--text-secondary)]">
                    {request.message}
                  </p>
                ) : null}
              </div>
              {request.status === "approved" ? null : (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={busyId === request.id}
                    onClick={() => void decide(request, "approve")}
                    aria-label={t("approveLabel", { email: request.email })}
                    data-testid={`join-request-approve-${request.email}`}
                  >
                    {t("approve")}
                  </Button>
                  {request.status === "pending" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busyId === request.id}
                      onClick={() => void decide(request, "decline")}
                      aria-label={t("declineLabel", { email: request.email })}
                      data-testid={`join-request-decline-${request.email}`}
                    >
                      {t("decline")}
                    </Button>
                  ) : null}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {pages > 1 ? (
        <nav className="flex items-center gap-3" aria-label={t("pageOf", { page, pages })}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("previous")}
          </Button>
          <Text tone="muted">{t("pageOf", { page, pages })}</Text>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("next")}
          </Button>
        </nav>
      ) : null}
    </div>
  );
}
